import { z } from "zod";

/**
 * Names a worker cannot take, because `/api/workers/:hostname` shares its level
 * with these static segments. A worker called `history` would shadow the
 * fleet-wide history route rather than getting its own detail page.
 *
 * `new` is here for the console rather than the API: `/workers/new` is the
 * add-worker page. Route order already matches the static path first, so this is
 * the belt to that braces — a worker named `new` would still be unreachable at
 * its own URL, and refusing the name says so at the point it is typed.
 */
export const RESERVED_WORKER_NAMES: readonly string[] = [
	"history",
	"images",
	"name-available",
	"new",
	"poll-now",
	"provision",
	"sessions",
];

const RESERVED_SET = new Set(RESERVED_WORKER_NAMES);

/** Shared refusal so every path that names a worker rejects the same words. */
export function isReservedWorkerName(name: string): boolean {
	return RESERVED_SET.has(name.trim().toLowerCase());
}

export const RESERVED_WORKER_NAME_MESSAGE = `Reserved by the workers API (${RESERVED_WORKER_NAMES.join(", ")}) — pick another name`;

/**
 * Worker name.
 *
 * It becomes the FleetHost hostname, the docker container name
 * (`veil-worker-<name>`) and the container's own hostname, and same-box workers
 * resolve each other by container name on the bridge network — so it has to be a
 * valid DNS label, not just a display string. Lowercase only: docker names are
 * case-sensitive while DNS is not, and mixing them invites collisions that only
 * show up at resolution time.
 */
export const WorkerNameSchema = z
	.string()
	.trim()
	.min(1, "Give the worker a name")
	.max(40, "Keep it under 40 characters")
	.regex(
		/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
		"Use lowercase letters, digits and dashes; start and end with a letter or digit",
	)
	.refine((v) => !isReservedWorkerName(v), RESERVED_WORKER_NAME_MESSAGE);

/**
 * Image version to deploy. `latest` is accepted as-is; anything else must look
 * like a version tag so a typo cannot become a silent pull of something else.
 */
export const WorkerImageTagSchema = z
	.string()
	.trim()
	.min(1)
	.max(64)
	.regex(
		/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/,
		"Not a valid image tag (letters, digits, dots, dashes, underscores)",
	);

export const ProvisionLocalWorkerSchema = z.object({
	/** Omit to let the server pick the next free local-worker-N. */
	hostname: WorkerNameSchema.optional(),
	imageTag: WorkerImageTagSchema.optional(),
});
export type ProvisionLocalWorkerInput = z.infer<
	typeof ProvisionLocalWorkerSchema
>;

/**
 * Longest private key this will accept.
 *
 * An RSA-4096 PEM is ~3.2KB and an ed25519 one ~400 bytes, so this is generous
 * by an order of magnitude. It exists to bound what a request can push through
 * the redactor and onto a temp file, not to judge the key.
 */
const MAX_PRIVATE_KEY_BYTES = 65_536;

/**
 * OpenSSH private key, PEM text.
 *
 * Its own exported schema because three request shapes carry one — the two
 * remote-provision routes and the key library's create — and the bound below is
 * a transport property rather than something any one of them decides.
 */
export const SshPrivateKeySchema = z
	.string()
	.min(1, "Paste the private key")
	.max(MAX_PRIVATE_KEY_BYTES, "That is far too long to be a private key");

/**
 * How to reach another machine over SSH.
 *
 * Shared by the wizard's form resolver, the two remote-provision routes and
 * lib/ssh/exec.ts (which re-exports it as SshConnectionSchema). One definition
 * because a mismatch between what the browser validates and what the shell-out
 * accepts surfaces as "invalid format" from OpenSSH, minutes into a deploy.
 *
 * The private key is the one field in this codebase that is a secret in a
 * REQUEST body rather than a response. It must never reach an operation log, an
 * error message, or Operation.inputs — which is why the routes validate with
 * this schema rather than an Elysia `t` body schema: Elysia's validation error
 * carries the offending value, and here the offending value is the key.
 */
export const SshTargetSchema = z.object({
	host: z.string().trim().min(1, "Give the host an address"),
	port: z.coerce.number().int().min(1).max(65535).default(22),
	user: z.string().trim().min(1).default("root"),
	/** OpenSSH private key, PEM text. */
	privateKey: SshPrivateKeySchema,
	/** Only needed for an encrypted key. */
	passphrase: z.string().nullable().default(null),
});
export type SshTarget = z.infer<typeof SshTargetSchema>;
export type SshTargetInput = z.input<typeof SshTargetSchema>;

/**
 * Where to reach a machine, without the credential.
 *
 * Split out from SshTargetSchema because a request now names its key instead of
 * always carrying one: the address is validated from the body, and the key is
 * resolved separately (from the library or from the body) into a full
 * SshTarget before anything connects.
 */
export const SshEndpointSchema = SshTargetSchema.omit({
	privateKey: true,
	passphrase: true,
});
export type SshEndpoint = z.infer<typeof SshEndpointSchema>;

/** Operator-facing name of a saved key. The only handle the picker shows. */
export const SshKeyNameSchema = z
	.string()
	.trim()
	.min(1, "Give the key a name")
	.max(80, "Keep it under 80 characters");

/**
 * How a request names the key it wants used.
 *
 * A union of two shapes rather than one object with everything optional,
 * because the two fail differently and the wizard has to be told which control
 * to point at: "pick a key" and "that is not a private key" are not the same
 * correction. `sshKeyId` is what distinguishes them — a body carrying it is
 * asking for a saved key, and one carrying `privateKey` is bringing its own.
 *
 * Deliberately not a `z.discriminatedUnion`: that needs a literal tag field in
 * every branch, and the wire contract the console is built against has no tag
 * to discriminate on.
 */
export const SavedSshKeyFields = {
	sshKeyId: z
		.string()
		.trim()
		.min(1, "Pick a saved key, or paste a private key instead"),
} as const;

export const InlineSshKeyFields = {
	privateKey: SshPrivateKeySchema,
	/** Only needed for an encrypted key. */
	passphrase: z.string().nullable().default(null),
	/**
	 * Non-empty to also add this key to the library under that name, so the
	 * next deploy can pick it instead of pasting again. Absent means ephemeral:
	 * the key lives in memory for the operation and is forgotten.
	 */
	saveAs: SshKeyNameSchema.optional(),
} as const;

/**
 * Read-only probe of a candidate host, before anything is created.
 *
 * `hostname` is optional and only used to report whether that worker's
 * directory already exists on the box — the check is useful before the operator
 * has settled on a name.
 *
 * Takes a key the same two ways the deploy does, so Test connection works with
 * a saved key and never round-trips the secret through the browser.
 */
const RemoteCheckFields = {
	...SshEndpointSchema.shape,
	hostname: WorkerNameSchema.optional(),
} as const;

export const ProvisionRemoteCheckSchema = z.union([
	z.object({ ...RemoteCheckFields, ...SavedSshKeyFields }),
	z.object({ ...RemoteCheckFields, ...InlineSshKeyFields }),
]);
export type ProvisionRemoteCheckInput = z.infer<
	typeof ProvisionRemoteCheckSchema
>;

const RemoteWorkerFields = {
	...SshEndpointSchema.shape,
	hostname: WorkerNameSchema,
	imageTag: WorkerImageTagSchema.optional(),
	/**
	 * Pipe get.docker.com into root's shell when Docker is absent. Defaults OFF
	 * and needs an explicit opt-in: it is standard practice and still not
	 * something to do silently on someone else's server.
	 */
	installDocker: z.boolean().default(false),
	/**
	 * Narrow inbound access to the worker port to the manager's egress address,
	 * discovered from the SSH connection itself rather than guessed.
	 */
	restrictFirewall: z.boolean().default(false),
	/** Deploy the updater-agent sidecar so this worker can be rolled remotely. */
	autoUpdate: z.boolean().default(false),
} as const;

export const ProvisionRemoteWorkerSchema = z.union([
	z.object({ ...RemoteWorkerFields, ...SavedSshKeyFields }),
	z.object({ ...RemoteWorkerFields, ...InlineSshKeyFields }),
]);
export type ProvisionRemoteWorkerInput = z.infer<
	typeof ProvisionRemoteWorkerSchema
>;

/**
 * The key half of either request, narrowed off the parsed body.
 *
 * One function so both routes and the pipeline agree on what "the key arrived
 * as a reference" means, rather than each testing for a field.
 */
export type SshKeySource =
	| { kind: "saved"; sshKeyId: string }
	| {
			kind: "inline";
			privateKey: string;
			passphrase: string | null;
			saveAs?: string;
	  };

export function readSshKeySource(
	input: ProvisionRemoteWorkerInput | ProvisionRemoteCheckInput,
): SshKeySource {
	if ("sshKeyId" in input) {
		return { kind: "saved", sshKeyId: input.sshKeyId };
	}
	return {
		kind: "inline",
		privateKey: input.privateKey,
		passphrase: input.passphrase,
		...(input.saveAs === undefined ? {} : { saveAs: input.saveAs }),
	};
}

/** One host key `ssh` pinned for an address, as ssh-keygen reports it. */
export interface RemoteHostKey {
	type: string;
	/** "SHA256:…" — the form ssh prints when it asks about an unknown host. */
	fingerprint: string;
	bits: number | null;
}

/** What a candidate host reported about itself. See parseRemoteCheckOutput. */
export interface RemoteCheckFacts {
	os: string | null;
	kernel: string | null;
	arch: string | null;
	hasSystemd: boolean;
	isRoot: boolean;
	hasSudo: boolean;
	hasDocker: boolean;
	dockerUsable: boolean;
	hasCompose: boolean;
	dockerVersion: string | null;
	/** Null when the host shipped no way to look. */
	workerPortBound: boolean | null;
	existingWorkerDir: boolean;
	/** False when the probe did not run to completion. */
	complete: boolean;
}

/**
 * `POST /api/workers/provision/remote/check`.
 *
 * A refused connection is a 200 with `connected: false`, not an HTTP error: the
 * wizard renders it beside the fields the operator has to fix, and a thrown
 * error would only get them a toast.
 */
export interface ProvisionRemoteCheckResponse {
	success: true;
	/** SSH got a shell on the box. */
	connected: boolean;
	/** Remote exit status. 255 is ssh's own "could not connect". */
	exitCode: number;
	/** What `StrictHostKeyChecking=accept-new` trusted for this address. */
	hostKey: RemoteHostKey | null;
	/** Null when the connection never came up. */
	facts: RemoteCheckFacts | null;
	/** Blockers, in the order they would stop a deploy. Empty means good to go. */
	problems: string[];
	/** Redacted failure detail when `connected` is false. */
	message: string | null;
}
